import { CommonModule } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { Component, Input } from '@angular/core';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import type { UserLocationResponse } from '@core-mkt/interfaces/geolocation';
import { CouponQueryResolverService } from '@core-mkt/services/coupon-query-resolver/coupon-query-resolver.service';
import { CouponUseCases } from '@core-mkt/services/coupon-query-resolver/coupon-use-cases';
import { EnvService } from '@core-mkt/services/env/env.service';
import { GeolocationService } from '@core-mkt/services/geolocation/geolocation.service';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import type { Product } from '@core-mkt/services/product-builder/product-builder.models';
import { ProductBuilderService } from '@core-mkt/services/product-builder/product-builder.service';
import { ProductService } from '@core-mkt/services/product/product.service';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '@core-mkt/shared/components/vision-button/vision-button/vision-button.component';
import { VisionModalService } from '@core-mkt/shared/components/vision-modal/vision-modal.service';
import { VisionSelectComponent } from '@core-mkt/shared/components/vision-select/vision-select/vision-select.component';
import type { VisionSelectOption } from '@core-mkt/shared/components/vision-select/vision-select/vision-select.models';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCircleCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { map, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { ProductBuilderTableRow, RadioOption } from '../study-plan-builder.models';
import { CourseTypes, type ProductBuilderCategory } from '../study-plan-builder.models';

@Component({
  selector: 'study-builder-form',
  templateUrl: './product-form.component.html',
  standalone: true,
  imports: [CommonModule, VisionButtonComponent, VisionSelectComponent, FontAwesomeModule],
})
export class ProductFormComponent implements OnInit, OnDestroy {
  // Craft data
  @Input() data: ProductBuilderCategory[];
  states: VisionSelectOption[] = [];
  products: Product[] = [];
  faTimes = faTimes;
  // Form elements
  availableCourseTypes: RadioOption[] = [];
  availableDurations: RadioOption[] = [];
  filteredProducts: Product[] = [];
  selectedState: string = null;
  selectedProduct: string = null;
  discountAmount: number;
  discountType: string;

  faCircleCheck = faCircleCheck;

  // Email Coupon
  emailCoupon: string = null; // Store email coupon if email capture event is triggered
  // Button data
  ctaButtonData: VisionButton = {
    bgTheme: 'bg-white',
    buttonText: 'Get Started',
    buttonLink: '',
    fullWidth: true,
    nofollow: false,
    noreferrer: false,
    noopener: false,
    newTab: false,
    useIcon: true,
  };
  ctaButtonDisabled = false;

  fakeProducts: ProductBuilderTableRow[] = [];
  private readonly ID_PRODUCT_1_WEEK  = 'rvS3ANWYXu0iruJB'; // 1 week product ID
  private readonly ID_PRODUCT_1_MONTH = 'f4Iu38amPF4ciw7U'; // 1 month product ID
  private readonly ID_PRODUCT_3_MONTHS = 'iZDiTB6PWixXVvXj'; // 3 months product ID

  stateFromGeolocation: string; // State from geolocation
  stateAbbrFromCookie: string; // State abbreviation from cookie
  isLoadingState = false; // Loading state for the select element
  private destroy$ = new Subject<void>();
  realState: string;

  globalCDIQueryParam: string;
  globalSalesQueryParam: string;
  constructor(
    private productBuilderService: ProductBuilderService,
    private env: EnvService,
    private pricingService: PricingService,
    private ps: ProductService,
    private modalService: VisionModalService,
    public geolocation: GeolocationService,
    private couponQueryResolverService: CouponQueryResolverService,
  ) {
    this.geolocation?.isLoading.pipe(takeUntil(this.destroy$)).subscribe((isLoading) => {
      this.isLoadingState = isLoading;
    });
    this.globalCDIQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.CDI);
    this.globalSalesQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.SALES);
  }

  ngOnInit(): void {
    this.filterProducts();
    this.states = this.productBuilderService.getStates(this.data[0]);

    if (!this.geolocation.getLocationCookie()) {
      // If we don't have a cookie, get the user location
      this.getUserLocation();
    } else {
      // If we have a cookie, get the user location from the cookie
      this.getUserLocationFromCookie();
    }

    let stateGeolocation = null;
    this.geolocation.stateAbbr.pipe(takeUntil(this.destroy$)).subscribe((stateAbbr) => {
      stateGeolocation = this.geolocation.getStateName(stateAbbr);
      if (stateGeolocation && stateGeolocation === this.realState) {
        this.setDefaultState(stateGeolocation);
      }
    });
  }

  setDefaultState(state: string): void {
    this.setSelectedState(state);
    this.setSelectedCourseType(CourseTypes.salesperson);
    this.pricingService.couponCodeEvent.pipe(takeUntil(this.destroy$)).subscribe((coupon: string) => {
      if (coupon && coupon.trim() !== '') {
        // Set email coupon from the coupon event
        this.emailCoupon = coupon;
        // Update the price with the coupon
        this.setSelectedState(state, true);
      }
    });
  }

  /**
   * Sets the selected state and updates the available products and course types accordingly.
   *
   * @param state - The state to be selected.
   * @param isEmailEvent - Optional parameter to indicate if the event is triggered by an email.
   *
   * This method performs the following actions:
   * 1. Sets the selected state.
   * 2. Determines the appropriate coupon based on whether the event is an email event.
   * 3. Fetches the available products for the selected state and coupon.
   * 4. Updates the discount amount and type based on the first available product.
   * 5. Updates the available course types.
   * 6. Sets the selected course type and product based on the fetched products.
   */
  setSelectedState(state: string, isEmailEvent?: boolean): void {
    //Update geolacation cookie with selected state
    const stateAbbr = this.geolocation.getStateAbbr(state);
    this.geolocation.updateLocationCookie(stateAbbr);

    this.selectedState = state;
    // If the event is triggered by an email, use the email coupon, otherwise use the product coupon override if it exists
    const coupon = this.data[0].productCouponOverride
      ? this.data[0].productCouponOverride
      : isEmailEvent
      ? this.emailCoupon
      : null;

    this.productBuilderService
      .getAvailableProducts(this.productBuilderService.getProductsPerState(state, this.data), coupon)
      .pipe(takeUntil(this.destroy$))
      .subscribe((products) => {
        this.manageSettingsProductBuilder(products);
        this.ctaButtonDisabled = false;
      });
  }

  setSelectedNoneState(): void {
    if (this.fakeProducts.length > 0) {
      this.productBuilderService
        .getAvailableProducts(this.fakeProducts)
        .pipe(takeUntil(this.destroy$))
        .subscribe((products) => {
          if (products.length > 0) {
            this.availableCourseTypes =
              this.productBuilderService.getAvailableCourseTypesAndDurations(products).availableCourseTypes;

            this.products = products;
            // Skip product selection to avoid async product data fetching in SSR
            this.setSelectedCourseType(products[0].courseType.value, true);
            this.geolocation.removeLocationCookie();
          }
        });
    }
    this.ctaButtonDisabled = true;
  }

  manageSettingsProductBuilder(products: Product[]): void {
    if (products.length > 0) {
      this.discountAmount = products[0].product.hasDiscount;
      this.discountType = products[0].product.discountType;

      this.availableCourseTypes =
        this.productBuilderService.getAvailableCourseTypesAndDurations(products).availableCourseTypes;

      this.products = products;
      this.setSelectedCourseType(products[0].courseType.value);
      this.setSelectedProduct(products[1].product.id);
    }
  }

  filterProducts(): void {
    if (this.data[0]?.productBuilderTable) {
      const realProducts = this.data[0].productBuilderTable.filter(product =>
        product.productId !== this.ID_PRODUCT_1_WEEK &&
        product.productId !== this.ID_PRODUCT_1_MONTH &&
        product.productId !== this.ID_PRODUCT_3_MONTHS
      );
      this.fakeProducts = this.data[0].productBuilderTable.filter(product =>
        product.productId === this.ID_PRODUCT_1_WEEK  ||
        product.productId === this.ID_PRODUCT_1_MONTH ||
        product.productId === this.ID_PRODUCT_3_MONTHS
      );
      this.data[0].productBuilderTable = realProducts;
    }
  }

  /**
   * Sets the selected course type and updates the available durations and filtered products accordingly.
   *
   * @param courseType - The type of course to be selected.
   * @param skipProductSelection - Optional flag to skip calling setSelectedProduct (e.g., when button is disabled).
   *
   * This method filters the products based on the provided course type. It updates the discount amount and type
   * for the selected course type. It also updates the available durations by calling the productBuilderService
   * and sets the selected product to the second product in the filtered list if available.
   */
  setSelectedCourseType(courseType: CourseTypes, skipProductSelection?: boolean): void {
    const filteredProducts: Product[] = [];
    this.products.forEach((product) => {
      if (product.courseType.value === courseType) {
        this.discountAmount = product.product.hasDiscount;
        this.discountType = product.product.discountType;

        filteredProducts.push(product);
      }
    });

    this.availableDurations =
      this.productBuilderService.getAvailableCourseTypesAndDurations(filteredProducts).availableDurations;

    if (!skipProductSelection) {
      this.setSelectedProduct(filteredProducts[1]?.product.id);
    }

    this.filteredProducts = filteredProducts;
  }

  /**
   * Sets the selected product based on the provided product ID.
   * Updates the CTA button link with the product ID and coupon code if available.
   *
   * @param productId - The ID of the product to be selected.
   */
  setSelectedProduct(productId: string): void {
    const selectedProduct = this.products.find((product) => product.product.id === productId);

    if (selectedProduct) {
      const { id, coupon } = selectedProduct.product;
      this.ctaButtonData = {
        ...this.ctaButtonData,
        buttonLink: `${this.env.get.xgritCheckoutUrl}?productId=${id}${
          coupon ? `&${this.globalCDIQueryParam}=${coupon}` : ''
        }`,
      };

      const productEntry: CraftXGritProductEntry = {
        xgritProductId: productId,
      };
      this.ps.getProductData(productEntry).then((product: XGritCompleteProduct) => {
        this.ctaButtonData.buttonAriaLabel = product.apiData.title ? product.apiData.title : undefined;
      });
      this.ctaButtonData.buttonText = selectedProduct.duration.buttonText ? selectedProduct.duration.buttonText : `Get ${selectedProduct.duration.label}`;
    }
  }

  get percentageLabel(): string {
    return this.discountType === 'PERCENT'
      ? `${this.discountAmount * 100}% Discount Applied`
      : `$${this.discountAmount} Off Discount Applied`;
  }

  activateModal(event: MouseEvent): void {
    event.preventDefault();
    const modalContent = `<div class="flex gap-3 flex-col">
      <h2 class="text-xl !text-vis-regular font-extrabold mb-0">100% Money-Back Guarantee</h2>
      <p class="text-baselg !text-vis-subtle font-normal">
        The real estate exam isn't easy, so we've made studying with PrepAgent as stress-free as possible. If you're not
        satisfied for any reason, contact us within 5 days of purchase, and we'll refund your money—no questions asked.
      </p>
    </div>`;
    this.modalService.openModal({ showModal: true, modalContent: modalContent });
  }

  get moneyGuaranteeLinkStyle(): string {
    // Note: This is a temporary text style for the AARP brand link, color could be renamed in the future
    return 'text-ace-teal-700 border-b-2 border-b-ace-teal-700';
  }
  get moneyGuaranteeLabel(): string {
    return '100% Money Back Guarantee';
  }

  /**
   * Retrieves the user's location from the cookie.
   * Sets the state abbreviation from the cookie and gets the corresponding state name from geolocation.
   * If the state name is available, it calls the handleStateChange method with the state name.
   * Finally, it sets the isLoadingState flag to false.
   */
  getUserLocationFromCookie(): void {
    this.stateAbbrFromCookie = this.geolocation.getLocationCookie();
    this.stateFromGeolocation = this.geolocation.getStateName(this.stateAbbrFromCookie);

    if (this.stateFromGeolocation) {
      this.setSelectedState(this.stateFromGeolocation);
    }

    this.geolocation.setIsLoadingState(false);
  }

  /**
   * Retrieves the user's location and performs necessary actions based on the location.
   */
  getUserLocation(): void {
    this.geolocation.setIsLoadingState(true);

    this.geolocation
      .getUserLocation()
      .pipe(
        map((location: UserLocationResponse) => {
          return location;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((state) => {
        const stateExists = this.states.find((s) => s.value === state.state);
        this.realState = stateExists ? stateExists.value.toString() : undefined;

        if (this.realState) {
          this.geolocation.setLocationCookie(state.stateabbr);
          this.geolocation.setStateAbbr(state.stateabbr);

          this.stateFromGeolocation = this.geolocation.getStateName(state.stateabbr);
          // If we have a state name, changeEvent is emitted
          if (this.stateFromGeolocation) {
            this.setSelectedState(this.stateFromGeolocation);
          }
        }
        else {
          this.setSelectedNoneState();
        }
        this.geolocation.setIsLoadingState(false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
